import type {
  AppSettings,
  Container,
  ConversionConfiguration,
  DosageTable,
  MeasuringCup,
  Product,
  RecommendationRule,
  SoilLevel,
  StainGuide,
  TemperatureGuidance,
  TextileCategory,
  WaterHardness,
} from '../domain/types';

export const DATABASE_SCHEMA_VERSION = 4;
export const BACKUP_SCHEMA_VERSION = 1;
export const SEED_TIMESTAMP = '2026-01-01T00:00:00.000Z';

export const SEED_SETTINGS: AppSettings = {
  id: 'app',
  appName: 'WaschPilot Schule',
  schoolName: '',
  language: 'de',
  theme: 'system',
  defaultWaterHardnessId: 'medium',
  waterHardnessLocked: false,
  welcomeDismissed: false,
  autoLockMinutes: 10,
  createdAt: SEED_TIMESTAMP,
  updatedAt: SEED_TIMESTAMP,
};

export const SOIL_LEVELS: readonly SoilLevel[] = [
  { id: 'light', name: { de: 'Leicht verschmutzt', ar: 'اتساخ خفيف' }, sortOrder: 1 },
  { id: 'normal', name: { de: 'Normal verschmutzt', ar: 'اتساخ عادي' }, sortOrder: 2 },
  { id: 'strong', name: { de: 'Stark verschmutzt', ar: 'اتساخ شديد' }, sortOrder: 3 },
  {
    id: 'veryStrong',
    name: { de: 'Sehr stark verschmutzt', ar: 'اتساخ شديد جداً' },
    sortOrder: 4,
  },
];

export const TEXTILE_CATEGORIES: readonly TextileCategory[] = [
  {
    id: 'whiteRobust',
    name: { de: 'Weiße, robuste Wäsche', ar: 'غسيل أبيض متين' },
    sortOrder: 1,
  },
  { id: 'colored', name: { de: 'Buntwäsche', ar: 'غسيل ملوّن' }, sortOrder: 2 },
  { id: 'delicate', name: { de: 'Feinwäsche', ar: 'غسيل رقيق' }, sortOrder: 3 },
  {
    id: 'wool',
    name: { de: 'Wolle / Wollmischung', ar: 'صوف / خليط صوفي' },
    sortOrder: 4,
  },
  {
    id: 'synthetic',
    name: { de: 'Synthetik / Mischgewebe', ar: 'أقمشة صناعية / مخلوطة' },
    sortOrder: 5,
  },
  {
    id: 'cottonLinen',
    name: { de: 'Baumwolle / Leinen', ar: 'قطن / كتان' },
    sortOrder: 6,
  },
  { id: 'unsure', name: { de: 'Nicht sicher', ar: 'غير متأكد' }, sortOrder: 7 },
];

const GENERAL_SAFETY_NOTES = [
  {
    de: 'Pflegeetikett der Textilien beachten.',
    ar: 'يجب الالتزام ببطاقة العناية بالمنسوجات.',
  },
  {
    de: 'Wäsche nach Farbe und Verschmutzungsgrad sortieren.',
    ar: 'افرز الغسيل حسب اللون ودرجة الاتساخ.',
  },
  {
    de: 'Reinigungschemikalien niemals mischen.',
    ar: 'لا تخلط مواد التنظيف الكيميائية أبداً.',
  },
  {
    de: 'Verpackungshinweise und Sicherheitsregeln der Schule beachten.',
    ar: 'التزم بتعليمات العبوة وقواعد السلامة في المدرسة.',
  },
];

export const SEED_PRODUCTS: readonly Product[] = [
  {
    id: 'vmpro-vollwaschmittel',
    name: { de: 'VMpro Vollwaschmittel', ar: 'VMpro Vollwaschmittel' },
    category: { de: 'Vollwaschmittel', ar: 'مسحوق غسيل شامل' },
    active: true,
    supportedSoilLevels: ['light', 'normal', 'strong'],
    instructions: [
      { de: 'Nur Hauptwäsche.', ar: 'للغسلة الرئيسية فقط.' },
      {
        de: 'Direkt in die Einspülkammer geben.',
        ar: 'ضع المسحوق مباشرة في درج الغسلة الرئيسية.',
      },
      {
        de: 'Wäsche nach Farbe und Verschmutzungsgrad sortieren.',
        ar: 'افرز الغسيل حسب اللون ودرجة الاتساخ.',
      },
      {
        de: 'Pflegeetikett der Textilien beachten.',
        ar: 'التزم ببطاقة العناية بالمنسوجات.',
      },
    ],
    safetyNotes: GENERAL_SAFETY_NOTES,
    sortOrder: 1,
    createdAt: SEED_TIMESTAMP,
    updatedAt: SEED_TIMESTAMP,
  },
  {
    id: 'feny-fein',
    name: { de: 'Feny fein', ar: 'Feny fein' },
    category: { de: 'Feinwaschmittel', ar: 'منظف للغسيل الرقيق' },
    active: true,
    supportedSoilLevels: ['light', 'normal', 'strong', 'veryStrong'],
    instructions: [
      {
        de: 'Pflegeetikett der Textilien beachten.',
        ar: 'التزم ببطاقة العناية بالمنسوجات.',
      },
      {
        de: 'Wäsche nach Farbe und Verschmutzungsgrad sortieren.',
        ar: 'افرز الغسيل حسب اللون ودرجة الاتساخ.',
      },
    ],
    safetyNotes: GENERAL_SAFETY_NOTES,
    sortOrder: 2,
    createdAt: SEED_TIMESTAMP,
    updatedAt: SEED_TIMESTAMP,
  },
];

const DOSAGES: Readonly<
  Record<string, Readonly<Record<string, Readonly<Record<string, number>>>>>
> = {
  'vmpro-vollwaschmittel': {
    soft: { light: 14, normal: 19, strong: 29 },
    medium: { light: 18, normal: 25, strong: 35 },
    hard: { light: 25, normal: 31, strong: 41 },
  },
  'feny-fein': {
    soft: { light: 20, normal: 29, strong: 51, veryStrong: 71 },
    medium: { light: 24, normal: 33, strong: 60, veryStrong: 80 },
    hard: { light: 31, normal: 51, strong: 71, veryStrong: 89 },
  },
};

export const SEED_DOSAGE_TABLES: readonly DosageTable[] = Object.entries(DOSAGES).flatMap(
  ([productId, waterEntries]) =>
    Object.entries(waterEntries).flatMap(([waterHardnessId, soilEntries]) =>
      Object.entries(soilEntries).map(([soilLevelId, mlPerKg]) => ({
        id: `${productId}-${waterHardnessId}-${soilLevelId}`,
        productId,
        waterHardnessId,
        soilLevelId: soilLevelId as DosageTable['soilLevelId'],
        mlPerKg,
      })),
    ),
);

export const SEED_WATER_HARDNESS: readonly WaterHardness[] = [
  {
    id: 'soft',
    name: { de: 'Weich', ar: 'ماء يسر' },
    description: { de: 'Bereich I, bis 7 °dH', ar: 'النطاق I، حتى 7 °dH' },
    minDh: null,
    maxDh: 7,
    active: true,
    sortOrder: 1,
  },
  {
    id: 'medium',
    name: { de: 'Mittel', ar: 'متوسط العسر' },
    description: { de: 'Bereich II, 7–14 °dH', ar: 'النطاق II، من 7 إلى 14 °dH' },
    minDh: 7,
    maxDh: 14,
    active: true,
    sortOrder: 2,
  },
  {
    id: 'hard',
    name: { de: 'Hart bis sehr hart', ar: 'عسر إلى شديد العسر' },
    description: { de: 'Bereiche III–IV, über 14 °dH', ar: 'النطاقان III–IV، أكثر من 14 °dH' },
    minDh: 14,
    maxDh: null,
    active: true,
    sortOrder: 3,
  },
];

export const SEED_CONTAINERS: readonly Container[] = [
  {
    id: 'red-tub',
    name: { de: 'Rote Wanne', ar: 'الحوض الأحمر' },
    tareWeightG: 2800,
    active: true,
    isDefault: true,
    sortOrder: 1,
  },
];

export const SEED_MEASURING_CUPS: readonly MeasuringCup[] = [
  {
    id: 'small-measuring-cup',
    name: { de: 'Kleiner Messbecher', ar: 'كوب قياس صغير' },
    capacityMl: 250,
    emptyWeightG: 36,
    active: true,
    sortOrder: 1,
  },
  {
    id: 'large-measuring-cup',
    name: { de: 'Großer Messbecher', ar: 'كوب قياس كبير' },
    capacityMl: 500,
    emptyWeightG: 68,
    active: true,
    sortOrder: 2,
  },
];

export const SEED_CONVERSION_CONFIGURATIONS: readonly ConversionConfiguration[] = SEED_PRODUCTS.map(
  (product) => ({
    productId: product.id,
    schoolGramsPerMl: 1,
    calibratedGramsPerMl: null,
    activeConversionMode: 'school',
    calibrationStatus: 'notCalibrated',
    calibrationDate: null,
    calibrationNote: '',
    updatedAt: SEED_TIMESTAMP,
  }),
);

export const SEED_TEMPERATURE_GUIDANCE: readonly TemperatureGuidance[] = [
  {
    id: 'vmpro-care-label',
    productId: 'vmpro-vollwaschmittel',
    textileCategoryIds: ['whiteRobust', 'colored', 'synthetic', 'cottonLinen'],
    guidance: {
      de: 'Temperatur nach Pflegeetikett und schulischer Vorgabe wählen.',
      ar: 'اختر درجة الحرارة حسب بطاقة العناية وتعليمات المدرسة.',
    },
    active: true,
    sortOrder: 1,
  },
  {
    id: 'feny-20-30',
    productId: 'feny-fein',
    textileCategoryIds: ['wool', 'delicate'],
    guidance: {
      de: '20–30 °C: Wolle, wollhaltige Materialien und Kunstseide.',
      ar: '20–30 °م: للصوف والمواد المحتوية على الصوف والحرير الصناعي.',
    },
    active: true,
    sortOrder: 1,
  },
  {
    id: 'feny-40',
    productId: 'feny-fein',
    textileCategoryIds: ['synthetic'],
    guidance: {
      de: '40 °C: synthetische Mischgewebe.',
      ar: '40 °م: للأقمشة الصناعية المخلوطة.',
    },
    active: true,
    sortOrder: 2,
  },
  {
    id: 'feny-60',
    productId: 'feny-fein',
    textileCategoryIds: ['cottonLinen'],
    guidance: { de: '60 °C: Baumwolle und Leinen.', ar: '60 °م: للقطن والكتان.' },
    active: true,
    sortOrder: 3,
  },
];

export const SEED_RECOMMENDATION_RULES: readonly RecommendationRule[] = [
  {
    id: 'recommend-white-vmpro',
    textileCategoryId: 'whiteRobust',
    colorScope: 'any',
    productId: 'vmpro-vollwaschmittel',
    note: { de: 'Schulempfehlung', ar: 'توصية المدرسة' },
    active: true,
    priority: 100,
  },
  {
    id: 'recommend-colored-feny',
    textileCategoryId: 'colored',
    colorScope: 'any',
    productId: 'feny-fein',
    note: { de: 'Schulempfehlung', ar: 'توصية المدرسة' },
    active: true,
    priority: 100,
  },
  {
    id: 'recommend-delicate-feny',
    textileCategoryId: 'delicate',
    colorScope: 'any',
    productId: 'feny-fein',
    note: { de: 'Schulempfehlung', ar: 'توصية المدرسة' },
    active: true,
    priority: 100,
  },
  {
    id: 'recommend-wool-feny',
    textileCategoryId: 'wool',
    colorScope: 'any',
    productId: 'feny-fein',
    note: { de: 'Schulempfehlung', ar: 'توصية المدرسة' },
    active: true,
    priority: 100,
  },
  {
    id: 'recommend-synthetic-feny',
    textileCategoryId: 'synthetic',
    colorScope: 'any',
    productId: 'feny-fein',
    note: { de: 'Schulempfehlung', ar: 'توصية المدرسة' },
    active: true,
    priority: 100,
  },
  {
    id: 'recommend-cotton-white-vmpro',
    textileCategoryId: 'cottonLinen',
    colorScope: 'white',
    productId: 'vmpro-vollwaschmittel',
    note: { de: 'Schulempfehlung für weiße Wäsche', ar: 'توصية المدرسة للغسيل الأبيض' },
    active: true,
    priority: 110,
  },
  {
    id: 'recommend-cotton-colored-feny',
    textileCategoryId: 'cottonLinen',
    colorScope: 'colored',
    productId: 'feny-fein',
    note: { de: 'Schulempfehlung für Buntwäsche', ar: 'توصية المدرسة للغسيل الملوّن' },
    active: true,
    priority: 110,
  },
  {
    id: 'recommend-unsure-ask',
    textileCategoryId: 'unsure',
    colorScope: 'any',
    productId: null,
    note: {
      de: 'Pflegeetikett prüfen oder die Lehrkraft fragen.',
      ar: 'تحقق من بطاقة العناية أو اسأل المعلمة.',
    },
    active: true,
    priority: 100,
  },
];

function stain(value: Omit<StainGuide, 'createdAt' | 'updatedAt' | 'active'>): StainGuide {
  return { ...value, active: true, createdAt: SEED_TIMESTAMP, updatedAt: SEED_TIMESTAMP };
}

export const SEED_STAIN_GUIDES: readonly StainGuide[] = [
  stain({
    id: 'blood',
    title: { de: 'Blut', ar: 'دم' },
    shortDescription: {
      de: 'Frische Blutflecken zuerst vorsichtig mit kaltem Wasser behandeln.',
      ar: 'عالج بقع الدم الحديثة أولاً بلطف باستخدام الماء البارد.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Pflegeetikett prüfen.', ar: 'تحقق من بطاقة العناية.' },
      {
        de: 'Fleck von der Rückseite mit kaltem Wasser ausspülen.',
        ar: 'اشطف البقعة من الجهة الخلفية بالماء البارد.',
      },
      {
        de: 'Bei Bedarf die Lehrkraft nach einer geeigneten Vorbehandlung fragen.',
        ar: 'عند الحاجة اسأل المعلمة عن معالجة أولية مناسبة.',
      },
    ],
    warnings: [
      { de: 'Empfindliche Fasern nicht stark reiben.', ar: 'لا تفرك الألياف الرقيقة بقوة.' },
    ],
    recommendedWaterTemperature: { de: 'Zuerst kaltes Wasser.', ar: 'استخدم الماء البارد أولاً.' },
    prohibitedActions: [
      { de: 'Nicht mit heißem Wasser beginnen.', ar: 'لا تبدأ بالماء الساخن.' },
      { de: 'Fleckenmittel nicht mischen.', ar: 'لا تخلط مزيلات البقع.' },
    ],
    recommendedProductId: null,
    sortOrder: 1,
  }),
  stain({
    id: 'grease-oil',
    title: { de: 'Fett / Öl', ar: 'دهون / زيت' },
    shortDescription: {
      de: 'Überschüssiges Fett vorsichtig aufnehmen und nicht verteilen.',
      ar: 'امتص الدهون الزائدة بحذر دون نشرها.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Pflegeetikett prüfen.', ar: 'تحقق من بطاقة العناية.' },
      {
        de: 'Überschuss mit saugfähigem Papier abtupfen.',
        ar: 'ربّت على الزيت الزائد بورق ماص.',
      },
      {
        de: 'Geeignete Vorbehandlung mit der Lehrkraft abstimmen.',
        ar: 'اتفق مع المعلمة على معالجة أولية مناسبة.',
      },
    ],
    warnings: [{ de: 'Farbechtheit vorher prüfen.', ar: 'اختبر ثبات اللون أولاً.' }],
    recommendedWaterTemperature: null,
    prohibitedActions: [
      { de: 'Nicht stark reiben.', ar: 'لا تفرك بقوة.' },
      { de: 'Keine unbekannten Lösemittel verwenden.', ar: 'لا تستخدم مذيبات غير معروفة.' },
    ],
    recommendedProductId: null,
    sortOrder: 2,
  }),
  stain({
    id: 'coffee-tea',
    title: { de: 'Kaffee / Tee', ar: 'قهوة / شاي' },
    shortDescription: {
      de: 'Flüssigkeit rasch aufnehmen und den Fleck vorsichtig ausspülen.',
      ar: 'امتص السائل سريعاً واشطف البقعة بحذر.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Mit einem sauberen Tuch abtupfen.', ar: 'ربّت بقطعة قماش نظيفة.' },
      { de: 'Von der Rückseite mit Wasser ausspülen.', ar: 'اشطف بالماء من الجهة الخلفية.' },
      { de: 'Nach Pflegeetikett waschen.', ar: 'اغسل وفق بطاقة العناية.' },
    ],
    warnings: [
      {
        de: 'Bei Milch im Getränk nicht erhitzen.',
        ar: 'لا تسخّن البقعة إذا احتوى المشروب على الحليب.',
      },
    ],
    recommendedWaterTemperature: null,
    prohibitedActions: [{ de: 'Fleck nicht trockenreiben.', ar: 'لا تفرك البقعة حتى تجف.' }],
    recommendedProductId: null,
    sortOrder: 3,
  }),
  stain({
    id: 'grass',
    title: { de: 'Gras', ar: 'عشب' },
    shortDescription: {
      de: 'Grasflecken vor dem Waschen vorsichtig prüfen und vorbehandeln.',
      ar: 'افحص بقع العشب وعالجها مبدئياً بحذر قبل الغسل.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'unsure'],
    steps: [
      { de: 'Pflegeetikett und Farbechtheit prüfen.', ar: 'تحقق من بطاقة العناية وثبات اللون.' },
      { de: 'Lose Rückstände vorsichtig entfernen.', ar: 'أزل البقايا السائبة بحذر.' },
      { de: 'Vorbehandlung mit der Lehrkraft abstimmen.', ar: 'نسّق المعالجة الأولية مع المعلمة.' },
    ],
    warnings: [
      { de: 'Empfindliche Stoffe der Lehrkraft zeigen.', ar: 'اعرض الأقمشة الحساسة على المعلمة.' },
    ],
    recommendedWaterTemperature: null,
    prohibitedActions: [
      { de: 'Keine Mittel unkontrolliert kombinieren.', ar: 'لا تجمع مواد مختلفة دون إشراف.' },
    ],
    recommendedProductId: null,
    sortOrder: 4,
  }),
  stain({
    id: 'make-up',
    title: { de: 'Make-up', ar: 'مستحضرات تجميل' },
    shortDescription: {
      de: 'Make-up nicht tiefer in die Fasern reiben.',
      ar: 'لا تفرك مستحضرات التجميل إلى داخل الألياف.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Überschuss vorsichtig abheben.', ar: 'ارفع الكمية الزائدة بحذر.' },
      { de: 'Mit sauberem Tuch abtupfen.', ar: 'ربّت بقطعة قماش نظيفة.' },
      { de: 'Geeignete Vorbehandlung erfragen.', ar: 'اسأل عن المعالجة الأولية المناسبة.' },
    ],
    warnings: [
      { de: 'Zuerst an unauffälliger Stelle testen.', ar: 'اختبر أولاً في موضع غير ظاهر.' },
    ],
    recommendedWaterTemperature: null,
    prohibitedActions: [{ de: 'Nicht kräftig einreiben.', ar: 'لا تفرك بقوة.' }],
    recommendedProductId: null,
    sortOrder: 5,
  }),
  stain({
    id: 'fruit-juice',
    title: { de: 'Obst / Saft', ar: 'فاكهة / عصير' },
    shortDescription: {
      de: 'Saft rasch aufnehmen und den Fleck von hinten ausspülen.',
      ar: 'امتص العصير بسرعة واشطف البقعة من الخلف.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Flüssigkeit abtupfen.', ar: 'ربّت لامتصاص السائل.' },
      {
        de: 'Mit kühlem Wasser von hinten ausspülen.',
        ar: 'اشطف من الخلف بماء فاتر مائل للبرودة.',
      },
      { de: 'Nach Pflegeetikett waschen.', ar: 'اغسل وفق بطاقة العناية.' },
    ],
    warnings: [{ de: 'Farbechtheit beachten.', ar: 'راعِ ثبات اللون.' }],
    recommendedWaterTemperature: {
      de: 'Zuerst kühles Wasser.',
      ar: 'ابدأ بماء فاتر مائل للبرودة.',
    },
    prohibitedActions: [{ de: 'Nicht mit Hitze fixieren.', ar: 'لا تثبّت البقعة بالحرارة.' }],
    recommendedProductId: null,
    sortOrder: 6,
  }),
  stain({
    id: 'ink',
    title: { de: 'Tinte', ar: 'حبر' },
    shortDescription: {
      de: 'Tintenflecken nicht verteilen und vor der Behandlung zeigen.',
      ar: 'لا تنشر بقعة الحبر واعرضها قبل المعالجة.',
    },
    allowedTextileCategoryIds: ['whiteRobust', 'colored', 'delicate', 'synthetic', 'cottonLinen'],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Saubere Unterlage unter den Fleck legen.', ar: 'ضع سطحاً نظيفاً تحت البقعة.' },
      { de: 'Vorsichtig abtupfen, nicht wischen.', ar: 'ربّت بحذر ولا تمسح.' },
      { de: 'Die Lehrkraft nach dem Tintetyp fragen.', ar: 'اسأل المعلمة عن نوع الحبر.' },
    ],
    warnings: [
      {
        de: 'Lösemittel können Farbe und Fasern schädigen.',
        ar: 'قد تتلف المذيبات اللون والألياف.',
      },
    ],
    recommendedWaterTemperature: null,
    prohibitedActions: [
      { de: 'Keine unbekannten Lösemittel verwenden.', ar: 'لا تستخدم مذيبات غير معروفة.' },
      { de: 'Nicht reiben.', ar: 'لا تفرك.' },
    ],
    recommendedProductId: null,
    sortOrder: 7,
  }),
  stain({
    id: 'unknown',
    title: { de: 'Unbekannter Fleck', ar: 'بقعة غير معروفة' },
    shortDescription: {
      de: 'Unbekannte Flecken vorsichtig behandeln und nicht experimentieren.',
      ar: 'تعامل بحذر مع البقع المجهولة ولا تجرّب مواد مختلفة.',
    },
    allowedTextileCategoryIds: [
      'whiteRobust',
      'colored',
      'delicate',
      'wool',
      'synthetic',
      'cottonLinen',
      'unsure',
    ],
    allowedColors: ['white', 'colored', 'delicate', 'unsure'],
    steps: [
      { de: 'Pflegeetikett prüfen.', ar: 'تحقق من بطاقة العناية.' },
      { de: 'Fleck nicht weiter bearbeiten.', ar: 'لا تواصل معالجة البقعة بنفسك.' },
      { de: 'Die Lehrkraft fragen.', ar: 'اسأل المعلمة.' },
    ],
    warnings: [{ de: 'Unbekannte Reaktionen sind möglich.', ar: 'قد تحدث تفاعلات غير معروفة.' }],
    recommendedWaterTemperature: null,
    prohibitedActions: [
      { de: 'Nicht mit mehreren Chemikalien experimentieren.', ar: 'لا تجرّب عدة مواد كيميائية.' },
      { de: 'Keine Hitze anwenden.', ar: 'لا تستخدم الحرارة.' },
    ],
    recommendedProductId: null,
    sortOrder: 8,
  }),
];

export const SEED_DATA = {
  settings: [SEED_SETTINGS],
  products: SEED_PRODUCTS,
  dosageTables: SEED_DOSAGE_TABLES,
  waterHardness: SEED_WATER_HARDNESS,
  containers: SEED_CONTAINERS,
  measuringCups: SEED_MEASURING_CUPS,
  conversionConfigurations: SEED_CONVERSION_CONFIGURATIONS,
  calibrationMeasurements: [],
  temperatureGuidance: SEED_TEMPERATURE_GUIDANCE,
  recommendationRules: SEED_RECOMMENDATION_RULES,
  stainGuides: SEED_STAIN_GUIDES,
} as const;
